import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Shield, Trash2, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Section, Eyebrow, FadeIn, PageHero } from '../components/ui-bits';
import Breadcrumb from '../components/Breadcrumb';

const LOGOS = {
  cis: 'https://customer-assets.emergentagent.com/job_bringing-light/artifacts/7tgkxuxg_logo%20CIS.png',
  cacho: 'https://customer-assets.emergentagent.com/job_bringing-light/artifacts/g6u180t8_logo%20cacho.png',
  inmobiliaria: 'https://customer-assets.emergentagent.com/job_bringing-light/artifacts/ypbcu85o_logo%20inmobiliaria.png',
  leitpal: 'https://customer-assets.emergentagent.com/job_bringing-light/artifacts/uldkzlzi_logo%20leitpalel.png',
  recetario: 'https://customer-assets.emergentagent.com/job_bringing-light/artifacts/jhamaw9s_logo%20recetario.png',
};

const DesarrolloSoftware = () => {
  const { t, lang } = useLanguage();

  const products = [
    { logo: LOGOS.cis, name: t.v4Page.cisTitle, desc: t.v4Page.cisDesc, tag: 'PROD 01', tone: 'from-amber-300/15 to-amber-500/5', alt: 'CIS logo', key: 'cis', marketing: '/cis' },
    {
      logo: LOGOS.cacho,
      name: t.v4Page.cachoTitle,
      desc: t.v4Page.cachoDesc,
      tag: 'PROD 02',
      tone: 'from-emerald-300/15 to-emerald-700/5',
      alt: 'Cachodice logo',
      key: 'cacho',
      marketing: '/cachodice',
      legal: [
        { to: '/cachodice/privacy', label: lang === 'en' ? 'Privacy Policy' : 'Política de Privacidad', icon: Shield, testid: 'cacho-privacy-link-card' },
        { to: '/cachodice/terms', label: lang === 'en' ? 'Terms of Service' : 'Términos y Condiciones', icon: FileText, testid: 'cacho-terms-link-card' },
      ],
    },
    { logo: LOGOS.inmobiliaria, name: t.v4Page.imTitle, desc: t.v4Page.imDesc, tag: 'PROD 03', tone: 'from-sky-300/15 to-sky-700/5', alt: 'InmobiliariaManager logo', key: 'im', marketing: '/inmobiliaria-manager' },
    {
      logo: LOGOS.leitpal,
      name: t.v4Page.leitTitle,
      desc: t.v4Page.leitDesc,
      tag: 'PROD 04',
      tone: 'from-indigo-300/15 to-indigo-700/5',
      alt: 'Leitpalel logo',
      key: 'leit',
      marketing: '/leitpalel',
      legal: [
        { to: '/leitpalel/privacy', label: lang === 'en' ? 'Privacy Policy' : 'Política de Privacidad', icon: Shield, testid: 'leitpal-privacy-link' },
        { to: '/leitpalel/terms', label: lang === 'en' ? 'Terms of Service' : 'Términos y Condiciones', icon: FileText, testid: 'leitpal-terms-link' },
        { to: '/leitpalel/delete-account', label: lang === 'en' ? 'Delete Account' : 'Eliminar Cuenta', icon: Trash2, testid: 'leitpal-delete-link' },
      ],
    },
    { logo: LOGOS.recetario, name: t.v4Page.recetarioTitle, desc: t.v4Page.recetarioDesc, tag: 'PROD 05', tone: 'from-rose-300/15 to-rose-700/5', alt: 'Recetario App logo', key: 'recetario', marketing: '/recetario' },
  ];

  return (
    <div data-testid="vertical-4-page">
      <Breadcrumb items={[
        { to: '/que-hacemos', label: lang === 'en' ? 'What We Do' : 'Qué Hacemos' },
        { to: null, label: t.verticals.v4.title },
      ]} />
      <PageHero eyebrow={t.v4Page.eyebrow} title={t.v4Page.title} intro={t.v4Page.intro} />

      <Section className="pb-24" dataTestId="v4-image-block">
        <FadeIn>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[16/7]">
            <img
              src="https://images.unsplash.com/photo-1762279388979-6a430989284c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
              alt="Abstract technology"
              className="absolute inset-0 w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070d18] via-[#070d18]/30 to-transparent" />
          </div>
        </FadeIn>
      </Section>

      <Section className="pb-32" dataTestId="v4-products">
        <FadeIn>
          <Eyebrow>{t.v4Page.productsTitle}</Eyebrow>
        </FadeIn>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {products.map((p, i) => (
            <FadeIn key={p.key} delay={i * 0.06}>
              <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-8 h-full card-hover relative overflow-hidden flex flex-col" data-testid={`v4-product-${i + 1}`}>
                <div className={`absolute -top-20 -right-20 w-56 h-56 rounded-full bg-gradient-to-br ${p.tone} blur-3xl pointer-events-none`} />
                <div className="relative flex-1">
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-16 h-16 rounded-2xl bg-white/95 border border-white/10 flex items-center justify-center overflow-hidden shadow-lg">
                      <img src={p.logo} alt={p.alt} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <span className="font-display text-xs text-white/40 tracking-widest">{p.tag}</span>
                  </div>
                  <h4 className="font-display font-bold text-2xl text-white tracking-tight">{p.name}</h4>
                  <p className="mt-4 font-body text-white/60 leading-relaxed">{p.desc}</p>
                  {p.marketing && (
                    <Link
                      to={p.marketing}
                      data-testid={`v4-marketing-${p.key}`}
                      className="mt-5 inline-flex items-center gap-1.5 text-sm text-amber-300 hover:text-amber-200"
                    >
                      {lang === 'en' ? 'Explore app' : 'Explorar app'} <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </div>

                {p.legal && (
                  <div className="relative mt-6 pt-6 border-t border-white/10 flex flex-wrap gap-2">
                    {p.legal.map((l) => {
                      const Icon = l.icon;
                      return (
                        <Link
                          key={l.to}
                          to={l.to}
                          data-testid={l.testid}
                          className="inline-flex items-center gap-2 text-xs text-white/65 hover:text-amber-300 px-3 py-1.5 rounded-full border border-white/10 hover:border-amber-300/40 transition-colors"
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {l.label}
                          <ArrowUpRight className="w-3 h-3 opacity-60" />
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default DesarrolloSoftware;
